import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, COLLECTIONS } from "@/lib/api";
import { PageHeader, DataTable, PrimaryButton, GhostButton, Card, Field, Input, Select, Textarea, DemoBadge } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import BulkImportModal from "@/components/BulkImportModal";
import CandidateScoreModal from "@/components/CandidateScoreModal";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, UploadSimple, DownloadSimple, FileArrowUp, ArrowSquareOut, ShieldWarning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

function DownloadBtn({ id }) {
  const handle = async () => {
    try {
      const res = await api.get(`/files/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "file"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download forbidden");
    }
  };
  return <button data-testid={`doc-download-${id}`} onClick={(e) => { e.stopPropagation(); handle(); }} className="text-[#FFB300] hover:underline text-[11px]"><DownloadSimple size={11} className="inline mr-0.5" />Get</button>;
}

function OpenSourceBtn({ doc }) {
  const url = doc.source_url || doc.official_tender_link;
  const label = doc.source_portal === "TED" ? "Open TED Notice" : "Open Source";
  return <a data-testid={`doc-open-source-${doc.id}`} href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#FFB300] hover:underline text-[11px]"><ArrowSquareOut size={11} className="inline mr-0.5" />{label}</a>;
}

import { API } from "@/lib/api";

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="bg-[#0A0A0A] border border-white/10 px-2 py-2 text-[10px] font-mono-data tracking-widest uppercase text-zinc-300 focus:outline-none focus:border-[#FFB300]">
      <option value="">{label}: ALL</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// Column presets per collection
const PRESETS = {
  opportunities: {
    title: "Opportunities",
    overline: "Approved Pipeline",
    detailPath: "/opportunities",
    cols: [
      { key: "opportunity_name", label: "Name", render: (r) => <span className="text-white">{r.opportunity_name}<DemoBadge show={r.is_demo} /></span> },
      { key: "opportunity_type", label: "Type" },
      { key: "country", label: "Country" },
      { key: "product_category", label: "Category" },
      { key: "deadline", label: "Deadline", mono: true },
      { key: "fit_score", label: "Fit", mono: true },
      { key: "estimated_value", label: "Value", mono: true, render: (r) => r.estimated_value ? `${r.currency || ""} ${r.estimated_value}` : "—" },
      { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
      { key: "bid_decision_status", label: "Decision", render: (r) => <StatusPill value={r.bid_decision_status || "Decision Needed"} /> },
    ],
    nameKey: "opportunity_name",
    createFields: ["opportunity_name", "opportunity_type", "country", "product_category", "division", "deadline", "estimated_value", "currency"],
  },
  candidate_opportunities: {
    title: "Candidate Opportunities", overline: "Staging · Pre-Pipeline", detailPath: "/candidates",
    cols: [
      { key: "candidate_name", label: "Name", render: (r) => <span className="text-white">{r.candidate_name}<DemoBadge show={r.is_demo} /></span> },
      { key: "country", label: "Country" },
      { key: "source_portal", label: "Source", render: (r) => r.source_portal || <span className="text-zinc-700">—</span> },
      { key: "tender_id_notice_id", label: "Tender #", mono: true, render: (r) => r.tender_id_notice_id ? <span className="font-mono-data text-[11px] text-zinc-300">{r.tender_id_notice_id}</span> : <span className="text-zinc-700">—</span> },
      { key: "deadline", label: "Deadline", mono: true },
      { key: "search_match_confidence", label: "Confidence", render: (r) => r.search_match_confidence ? <StatusPill value={r.search_match_confidence} /> : (r.ai_score_label ? <StatusPill value={r.ai_score_label} /> : <span className="text-zinc-700">—</span>) },
      { key: "manual_review_priority", label: "Priority", render: (r) => r.manual_review_priority ? <StatusPill value={r.manual_review_priority} /> : <span className="text-zinc-700">—</span> },
      { key: "source_verification_status", label: "Verify", render: (r) => r.source_verification_status ? <StatusPill value={r.source_verification_status} /> : ((r.requires_web_verification || r.cannot_import_as_opportunity) ? <StatusPill value="Hold" /> : <span className="text-zinc-700">—</span>) },
      { key: "manual_download_required", label: "MD", render: (r) => r.manual_download_required ? <DownloadSimple size={13} className="text-[#FFB300]" title="Manual Download Required" data-testid={`cand-md-${r.id}`} /> : <span className="text-zinc-700">—</span> },
      { key: "duplicate_check", label: "Dup", render: (r) => <StatusPill value={r.duplicate_check} /> },
      { key: "review_status", label: "Status", render: (r) => <StatusPill value={r.review_status} /> },
      { key: "_src", label: "", render: (r) => (r.official_tender_link ? <a href={r.official_tender_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#FFB300] hover:underline text-[11px]" data-testid={`cand-source-${r.id}`}><ArrowSquareOut size={11} className="inline mr-0.5" />Source</a> : <span className="text-zinc-700">—</span>) },
    ],
    nameKey: "candidate_name",
    createFields: ["candidate_name", "opportunity_type", "country", "buyer_contracting_authority", "product_category", "division", "deadline", "estimated_value", "currency", "source_portal", "official_tender_link"],
  },
  search_rules: {
    title: "Search Rules", overline: "Discovery Configuration", detailPath: "/search-rules",
    cols: [
      { key: "rule_name", label: "Rule", render: (r) => <span className="text-white">{r.rule_name}<DemoBadge show={r.is_demo} /></span> },
      { key: "product_category", label: "Category" },
      { key: "division", label: "Division" },
      { key: "countries_regions", label: "Regions" },
      { key: "priority", label: "Priority", render: (r) => <StatusPill value={r.priority} /> },
      { key: "automation_readiness", label: "Automation", render: (r) => r.automation_readiness ? <StatusPill value={r.automation_readiness} /> : <StatusPill value={r.automation_enabled ? "Active" : "Off"} /> },
      { key: "active", label: "Active", render: (r) => <StatusPill value={r.active ? "Yes" : "No"} /> },
    ],
    nameKey: "rule_name",
    createFields: ["rule_name", "product_category", "division", "countries_regions", "inclusion_keywords", "exclusion_keywords", "priority", "search_frequency", "active"],
  },
  search_runs: {
    title: "Search Runs", overline: "Discovery Logs",
    cols: [
      { key: "search_run_name", label: "Run" },
      { key: "run_date", label: "Date", mono: true },
      { key: "sources_searched", label: "Sources" },
      { key: "result_count", label: "Results", mono: true },
      { key: "candidates_added", label: "Candidates", mono: true },
      { key: "duplicates_found", label: "Duplicates", mono: true },
      { key: "run_status", label: "Status", render: (r) => <StatusPill value={r.run_status} /> },
    ],
    nameKey: "search_run_name",
    createFields: ["search_run_name", "run_date", "sources_searched", "search_terms_used", "result_count", "candidates_added", "duplicates_found", "run_status"],
  },
  search_intelligence: {
    title: "Search Intelligence Library", overline: "Knowledge Base",
    cols: [
      { key: "intelligence_name", label: "Intelligence", render: (r) => <span className="text-white">{r.intelligence_name}<DemoBadge show={r.is_demo} /></span> },
      { key: "product_category", label: "Category" },
      { key: "source_type", label: "Source" },
      { key: "review_status", label: "Review", render: (r) => <StatusPill value={r.review_status} /> },
      { key: "confidence", label: "Confidence", render: (r) => <StatusPill value={r.confidence} /> },
    ],
    nameKey: "intelligence_name",
    createFields: ["intelligence_name", "product_category", "division", "source_type", "review_status", "confidence", "inclusion_keywords", "exclusion_keywords"],
  },
  tasks: {
    title: "Tasks", overline: "Action Items",
    cols: [
      { key: "task_name", label: "Task" },
      { key: "opportunity_id", label: "Opportunity", mono: true, render: (r) => r.opportunity_id ? r.opportunity_id.slice(0, 8) : "—" },
      { key: "due_date", label: "Due", mono: true },
      { key: "priority", label: "Priority", render: (r) => <StatusPill value={r.priority} /> },
      { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
    ],
    nameKey: "task_name",
    createFields: ["task_name", "opportunity_id", "due_date", "priority", "status", "description"],
  },
  documents: {
    title: "Documents", overline: "Files & Links",
    cols: [
      { key: "document_name", label: "Document" },
      { key: "document_type", label: "Type" },
      { key: "document_status", label: "Status", render: (r) => <StatusPill value={r.document_status} /> },
      { key: "supplier_visible", label: "Supplier Visible", render: (r) => <StatusPill value={r.supplier_visible ? "Yes" : "No"} /> },
      { key: "source_url", label: "Source", render: (r) => r.source_url ? <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[#FFB300] underline break-all">link</a> : "—" },
      { key: "_dl", label: "", render: (r) => r.storage_path ? <DownloadBtn id={r.id} /> : ((r.source_url || r.official_tender_link) ? <OpenSourceBtn doc={r} /> : null) },
    ],
    nameKey: "document_name",
    createFields: ["document_name", "document_type", "opportunity_id", "source_url", "google_drive_folder_link", "document_status", "supplier_visible"],
  },
  buyers: {
    title: "Buyers", overline: "Contracting Authorities",
    cols: [
      { key: "buyer_name", label: "Buyer" },
      { key: "country", label: "Country" },
      { key: "website", label: "Website", render: (r) => r.website ? <a href={r.website} target="_blank" rel="noreferrer" className="text-[#FFB300] underline">link</a> : "—" },
      { key: "contact_info", label: "Contact" },
    ],
    nameKey: "buyer_name",
    createFields: ["buyer_name", "country", "contact_info", "website", "source_portals", "notes"],
  },
  requirements: {
    title: "Requirements", overline: "Compliance & Specs",
    cols: [
      { key: "requirement_id_label", label: "ID" },
      { key: "opportunity_id", label: "Opportunity", mono: true, render: (r) => r.opportunity_id ? r.opportunity_id.slice(0,8) : "—" },
      { key: "requirement_type", label: "Type" },
      { key: "required_standards", label: "Standards" },
      { key: "supplier_visible", label: "Supplier", render: (r) => <StatusPill value={r.supplier_visible ? "Yes" : "No"} /> },
    ],
    nameKey: "requirement_id_label",
    createFields: ["requirement_id_label", "opportunity_id", "requirement_type", "required_standards", "required_documents", "supplier_visible"],
  },
  awards: {
    title: "Awards", overline: "Outcomes",
    cols: [
      { key: "opportunity_id", label: "Opportunity", mono: true, render: (r) => r.opportunity_id ? r.opportunity_id.slice(0,8) : "—" },
      { key: "award_date", label: "Date", mono: true },
      { key: "outcome", label: "Outcome", render: (r) => <StatusPill value={r.outcome} /> },
      { key: "winner", label: "Winner" },
      { key: "award_value", label: "Value", mono: true, render: (r) => r.award_value ? `${r.currency || ""} ${r.award_value}` : "—" },
    ],
    nameKey: "outcome",
    createFields: ["opportunity_id", "award_date", "outcome", "winner", "award_value", "currency"],
  },
  suppliers: {
    title: "Suppliers", overline: "Production Network",
    cols: [
      { key: "supplier_name", label: "Supplier" },
      { key: "country", label: "Country" },
      { key: "products_supplied", label: "Products" },
      { key: "supplier_status", label: "Status", render: (r) => <StatusPill value={r.supplier_status} /> },
      { key: "contact_email", label: "Contact" },
    ],
    nameKey: "supplier_name",
    createFields: ["supplier_name", "country", "contact_name", "contact_email", "products_supplied", "capabilities", "supplier_status", "portal_login_email"],
  },
  opportunity_suppliers: {
    title: "Supplier Assignments", overline: "Opportunity × Supplier",
    cols: [
      { key: "assignment_name", label: "Assignment" },
      { key: "opportunity_id", label: "Opp", mono: true, render: (r) => r.opportunity_id ? r.opportunity_id.slice(0,8) : "—" },
      { key: "supplier_id", label: "Supplier", mono: true, render: (r) => r.supplier_id ? r.supplier_id.slice(0,8) : "—" },
      { key: "assignment_status", label: "Status", render: (r) => <StatusPill value={r.assignment_status} /> },
      { key: "supplier_portal_visible", label: "Visible", render: (r) => <StatusPill value={r.supplier_portal_visible ? "Yes" : "No"} /> },
    ],
    nameKey: "assignment_name",
    createFields: ["assignment_name", "opportunity_id", "supplier_id", "assignment_status", "supplier_portal_visible", "sample_due_date"],
  },
  supplier_samples: {
    title: "Supplier Samples", overline: "Sample Tracker",
    cols: [
      { key: "sample_name", label: "Sample" },
      { key: "quantity_requested", label: "Qty", mono: true },
      { key: "sample_due_date", label: "Due", mono: true },
      { key: "sample_status", label: "Status", render: (r) => <StatusPill value={r.sample_status} /> },
      { key: "testing_status", label: "Testing", render: (r) => <StatusPill value={r.testing_status} /> },
    ],
    nameKey: "sample_name",
    createFields: ["sample_name", "opportunity_supplier_id", "supplier_id", "opportunity_id", "quantity_requested", "sample_due_date", "sample_status", "testing_required"],
  },
  supplier_deliverables: {
    title: "Supplier Deliverables", overline: "Files & Reports",
    cols: [
      { key: "deliverable_name", label: "Name" },
      { key: "deliverable_type", label: "Type" },
      { key: "due_date", label: "Due", mono: true },
      { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> },
      { key: "supplier_visible", label: "Supplier", render: (r) => <StatusPill value={r.supplier_visible ? "Yes" : "No"} /> },
    ],
    nameKey: "deliverable_name",
    createFields: ["deliverable_name", "deliverable_type", "opportunity_id", "supplier_id", "due_date", "status", "supplier_visible"],
  },
  supplier_production: {
    title: "Supplier Production", overline: "Production Floor",
    cols: [
      { key: "production_tracker_name", label: "Tracker" },
      { key: "production_status", label: "Status", render: (r) => <StatusPill value={r.production_status} /> },
      { key: "quality_control_status", label: "QC", render: (r) => <StatusPill value={r.quality_control_status} /> },
      { key: "shipping_status", label: "Shipping", render: (r) => <StatusPill value={r.shipping_status} /> },
      { key: "planned_completion_date", label: "ETA", mono: true },
    ],
    nameKey: "production_tracker_name",
    createFields: ["production_tracker_name", "opportunity_id", "supplier_id", "production_status", "production_quantity", "planned_start_date", "planned_completion_date"],
  },
  supplier_questions: {
    title: "Supplier Questions", overline: "Q&A Log",
    cols: [
      { key: "question_title", label: "Question" },
      { key: "supplier_id", label: "Supplier", mono: true, render: (r) => r.supplier_id ? r.supplier_id.slice(0,8) : "—" },
      { key: "question_status", label: "Status", render: (r) => <StatusPill value={r.question_status} /> },
      { key: "asked_date", label: "Asked", mono: true },
    ],
    nameKey: "question_title",
    createFields: ["question_title", "opportunity_id", "supplier_id", "question", "question_status"],
  },
};

const SELECT_OPTIONS = {
  opportunity_type: ["Tender","RFP","RFQ","RFI","Framework","Pre-Solicitation","Market Survey","Award Notice","Other"],
  product_category: ["Ballistic Vests","Ballistic Plates","Ballistic Helmets","EV Fire Blankets","General / Kitchen / Industrial Fire Blankets","Battery Fire Containment","Polyurea / Coatings","Food / Institutional Supply","Other"],
  division: ["Defense","Thermal / Fire Safety","Coatings","Food","Other"],
  currency: ["USD","EUR","GBP","CAD","AUD","Other"],
  priority: ["Critical","High","Medium","Low"],
  search_frequency: ["Daily","Weekly","Monthly"],
  status: ["New","Needs Review","Qualified","Documents Downloaded","Questions Needed","Bid/No-Bid Pending","Preparing Bid","Submitted","Award Watch","Won","Lost","No-Bid","Archived"],
  review_status: ["Draft","Needs Review","Approved","Needs Update","Archived"],
  confidence: ["High","Medium","Low","Unverified"],
  source_type: ["Search Playbook","Prior Chat","Purpose-Built GPT","Manual Research","Official Portal","Buyer Website","Market Intelligence","Other"],
  requirement_type: ["Standard","Document","Testing","Warranty","Delivery Terms","Submission Method","Evaluation Criteria","Other"],
  document_status: ["Needed","Requested","Downloaded","Uploaded","Reviewed","Missing","Manual Download Required"],
  outcome: ["Won","Lost","Shortlisted","Canceled","Unknown"],
  supplier_status: ["Prospect","Active","Preferred","On Hold","Do Not Use"],
  assignment_status: ["Invited","Reviewing","Interested","Declined","Sample Requested","Sample Submitted","Under Testing","Approved for Bid","Not Selected","Awarded Production","Completed"],
  sample_status: ["Not Requested","Requested","In Production","Shipped","Received","Under Review","Approved","Rejected","Resubmission Needed"],
  testing_required: ["Yes","No","Unclear"],
  deliverable_type: ["Technical Specification","Test Report","Certificate","Compliance Declaration","Warranty","Company Document","Production Plan","Packaging Info","Shipping Document","Other"],
  production_status: ["Not Started","Planning","Materials Sourcing","In Production","Quality Control","Ready to Ship","Shipped","Completed","Delayed"],
  question_status: ["New","Under Review","Answered","Waiting on Buyer","Closed"],
  run_status: ["Planned","Running","Completed","Needs Review","Failed"],
  taiwan_eligibility: ["Yes","Likely","Unclear","Likely No","No"],
  samples_required: ["Yes","No","Unclear"],
  bond_required: ["Yes","No","Unclear"],
  local_partner_needed: ["Yes","No","Unclear"],
  duplicate_check: ["Unique","Possible Duplicate","Duplicate","Unclear"],
};

export default function ListPage({ collection }) {
  const preset = PRESETS[collection];
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const nav = useNavigate();
  const { can, user } = useAuth();
  const detail = preset?.detailPath;
  const canCreate = can(collection, "create");
  const canUpload = collection === "documents" && ["owner","admin","opportunity_manager","liaison","tender_analyst"].includes(user?.role);
  const [bulkOpen, setBulkOpen] = useState(false);
  const canBulkImport = collection === "candidate_opportunities" && ["owner","admin","tender_analyst"].includes(user?.role);
  const canScore = collection === "candidate_opportunities" && ["owner","admin","tender_analyst"].includes(user?.role);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [scoreOpen, setScoreOpen] = useState(false);
  const [prevCollection, setPrevCollection] = useState(collection);
  const isCandidates = collection === "candidate_opportunities";
  const [cf, setCf] = useState({});       // candidate quick-filters
  const [vQueue, setVQueue] = useState(false); // Verification Queue toggle

  if (collection !== prevCollection) {
    setPrevCollection(collection);
    setRows([]); setSearch(""); setCreating(false); setForm({}); setSelectedIds(new Set());
    setCf({}); setVQueue(false);
  }

  useEffect(() => {
    let active = true;
    api.get(`/collections/${collection}`).then((r) => { if (active) setRows(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [collection]);

  const reloadRows = () => api.get(`/collections/${collection}`).then((r) => setRows(r.data)).catch(() => {});
  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const inVerificationQueue = (r) => {
    if (r.source_verification_status === "Verified") return false;
    return r.requires_web_verification === true || r.cannot_import_as_opportunity === true
      || r.manual_download_required === true || r.manual_review_priority === "High"
      || ["Needs More Info", "Rejected"].includes(r.source_verification_status);
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
    }
    if (isCandidates) {
      if (vQueue) out = out.filter(inVerificationQueue);
      if (cf.confidence) out = out.filter((r) => r.search_match_confidence === cf.confidence);
      if (cf.priority) out = out.filter((r) => r.manual_review_priority === cf.priority);
      if (cf.manual_download) out = out.filter((r) => cf.manual_download === "Yes" ? r.manual_download_required === true : r.manual_download_required !== true);
      if (cf.verify) out = out.filter((r) => cf.verify === "Unverified" ? !r.source_verification_status : r.source_verification_status === cf.verify);
      if (cf.duplicate) out = out.filter((r) => (r.duplicate_check || "Unique") === cf.duplicate);
      if (cf.source_portal) out = out.filter((r) => r.source_portal === cf.source_portal);
      if (cf.product_category) out = out.filter((r) => r.product_category === cf.product_category);
    }
    return out;
  }, [rows, search, isCandidates, vQueue, cf]);

  const toggleAll = (checked) => setSelectedIds(checked ? new Set(filtered.map((r) => r.id)) : new Set());

  const submitCreate = async () => {
    try {
      const r = await api.post(`/collections/${collection}`, { data: form });
      toast.success("Created");
      setCreating(false);
      setForm({});
      setRows([r.data, ...rows]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  if (!preset) return <div className="p-8 text-zinc-500">Unknown collection</div>;

  return (
    <div>
      <PageHeader
        overline={preset.overline}
        title={preset.title}
        sub={`${filtered.length}${filtered.length !== rows.length ? ` of ${rows.length}` : ""} record${rows.length === 1 ? "" : "s"}`}
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-500" />
              <input
                data-testid="list-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="FILTER"
                className="bg-[#0A0A0A] border border-white/10 pl-7 pr-2 py-2 text-[11px] font-mono-data tracking-widest uppercase text-white focus:outline-none focus:border-[#FFB300] w-[180px]"
              />
            </div>
            {canBulkImport && <GhostButton onClick={() => setBulkOpen(true)} data-testid="bulk-import-open-btn"><FileArrowUp size={12} className="inline mr-1" />Bulk Import</GhostButton>}
            {canScore && <GhostButton onClick={() => setScoreOpen(true)} disabled={selectedIds.size === 0} data-testid="ai-score-selected-btn" title={selectedIds.size === 0 ? "Select candidates to score" : ""}><Sparkle size={12} className="inline mr-1" />AI Score Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}</GhostButton>}
            <PrimaryButton data-testid="list-new-btn" onClick={() => setCreating(true)} disabled={!canCreate} title={!canCreate ? "Your role cannot create here" : ""}><Plus size={12} className="inline mr-1" /> New</PrimaryButton>
          </>
        }
      />
      <div className="p-8">
        <BulkImportModal open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={() => api.get(`/collections/${collection}`).then((r) => setRows(r.data))} />
        {canScore && (
          <CandidateScoreModal
            open={scoreOpen}
            candidateIds={Array.from(selectedIds)}
            onClose={() => setScoreOpen(false)}
            onDone={() => { reloadRows(); setSelectedIds(new Set()); }}
          />
        )}
        {creating && (
          <Card className="p-4 mb-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">New {preset.title.slice(0,-1)}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {preset.createFields.map((f) => (
                <Field key={f} label={f.replace(/_/g, " ")}>
                  {SELECT_OPTIONS[f] ? (
                    <Select value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })}>
                      <option value="">—</option>
                      {SELECT_OPTIONS[f].map((o) => <option key={o} value={o}>{o}</option>)}
                    </Select>
                  ) : f === "active" || f === "supplier_visible" || f === "supplier_portal_visible" ? (
                    <Select value={String(form[f] ?? false)} onChange={(e) => setForm({ ...form, [f]: e.target.value === "true" })}>
                      <option value="false">No</option><option value="true">Yes</option>
                    </Select>
                  ) : f === "description" || f === "notes" || f === "question" ? (
                    <Textarea rows={3} value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
                  ) : (
                    <Input type={f.includes("date") ? "date" : f.includes("value") || f.includes("score") || f.includes("count") || f.includes("quantity") ? "number" : "text"}
                           value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
                  )}
                </Field>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <GhostButton onClick={() => { setCreating(false); setForm({}); }} data-testid="create-cancel-btn">Cancel</GhostButton>
              <PrimaryButton onClick={submitCreate} data-testid="create-submit-btn">Create</PrimaryButton>
            </div>
          </Card>
        )}

        {isCandidates && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="candidate-filter-bar">
            <button onClick={() => setVQueue((v) => !v)} data-testid="verification-queue-toggle"
              className={`text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${vQueue ? "border-[#FFB300] text-[#FFB300] bg-[#FFB300]/10" : "border-white/15 text-zinc-400 hover:text-zinc-200"}`}>
              <ShieldWarning size={12} className="inline mr-1" /> Verification Queue ({rows.filter(inVerificationQueue).length})
            </button>
            <FilterSelect label="Confidence" value={cf.confidence} onChange={(v) => setCf({ ...cf, confidence: v })} options={["High Confidence", "Medium Confidence", "Low Confidence", "Rejected / Noise"]} testid="cf-confidence" />
            <FilterSelect label="Priority" value={cf.priority} onChange={(v) => setCf({ ...cf, priority: v })} options={["High", "Medium", "Low"]} testid="cf-priority" />
            <FilterSelect label="Manual DL" value={cf.manual_download} onChange={(v) => setCf({ ...cf, manual_download: v })} options={["Yes", "No"]} testid="cf-md" />
            <FilterSelect label="Verification" value={cf.verify} onChange={(v) => setCf({ ...cf, verify: v })} options={["Unverified", "Verified", "Needs More Info", "Rejected"]} testid="cf-verify" />
            <FilterSelect label="Duplicate" value={cf.duplicate} onChange={(v) => setCf({ ...cf, duplicate: v })} options={["Unique", "Possible Duplicate", "Duplicate", "Unclear"]} testid="cf-dup" />
            <FilterSelect label="Source" value={cf.source_portal} onChange={(v) => setCf({ ...cf, source_portal: v })} options={["TED", "Find a Tender", "SAM.gov", "Contracts Finder", "Other"]} testid="cf-source" />
            <FilterSelect label="Category" value={cf.product_category} onChange={(v) => setCf({ ...cf, product_category: v })} options={SELECT_OPTIONS.product_category} testid="cf-category" />
            {(vQueue || Object.values(cf).some(Boolean)) && (
              <button onClick={() => { setCf({}); setVQueue(false); }} data-testid="cf-clear" className="text-[10px] uppercase tracking-widest px-2 py-2 text-zinc-500 hover:text-zinc-300">Clear</button>
            )}
          </div>
        )}

        <DataTable
          testId={`list-${collection}-table`}
          columns={preset.cols}
          rows={filtered}
          onRowClick={detail ? (r) => nav(`${detail}/${r.id}`) : undefined}
          selectable={canScore}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
        />
      </div>
    </div>
  );
}
